/**
 * The phone sign-off form shared by the EW field ticket and change-order pages
 * (EW-5): the signer types their name (and optionally title) and signs on the
 * pad. Submit stays disabled until BOTH a name and a signature are present
 * (`ewCanSubmitSignature`) — the API refuses either missing, and a disabled
 * button says so before the round trip.
 */
import { useState } from 'react';
import { PenLine } from 'lucide-react';
import SignaturePad from '../../../components/SignaturePad';
import { ewCanSubmitSignature } from '../../../lib/ew-tickets';
import type { EwPhoneSignWrite } from '../../../types/earthworks';
import { PT_FIELD_PRIMARY, PtFieldCard } from '../../pressure-testing/field/PtFieldBits';

const FIELD_INPUT = 'h-12 w-full rounded-fi-control border border-fieldiq-border bg-fieldiq-dark px-3 text-base text-white';

export function EwSignForm({
  heading,
  intro,
  submitLabel,
  pending,
  error,
  onSubmit,
  testId,
}: {
  heading: string;
  intro: string;
  submitLabel: string;
  pending: boolean;
  error: string | null;
  onSubmit: (body: EwPhoneSignWrite) => void;
  testId: string;
}) {
  const [name, setName] = useState('');
  const [title, setTitle] = useState('');
  const [signature, setSignature] = useState<string | null>(null);
  const ready = ewCanSubmitSignature(name, signature);

  return (
    <PtFieldCard>
      <div className="space-y-3 px-4 py-4" data-testid={testId}>
        <p className="text-base font-bold text-white">{heading}</p>
        <p className="text-sm text-fieldiq-text">{intro}</p>
        {error && <p role="alert" className="rounded-fi-control border border-fi-red/50 bg-fi-red/10 px-3 py-2 text-sm text-white">{error}</p>}
        <label className="block text-sm text-fieldiq-muted">
          Signer name
          <input className={FIELD_INPUT} aria-label="Signer name" value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
        </label>
        <label className="block text-sm text-fieldiq-muted">
          Title (optional)
          <input className={FIELD_INPUT} aria-label="Signer title" value={title} onChange={(e) => setTitle(e.target.value)} />
        </label>
        <SignaturePad label="Signature" value={signature ?? undefined} onSave={(d) => setSignature(d)} onClear={() => setSignature(null)} />
        <button
          type="button"
          className={PT_FIELD_PRIMARY}
          disabled={!ready || pending}
          data-testid={`${testId}-submit`}
          onClick={() => {
            if (!signature) return;
            onSubmit({ signer_name: name.trim(), signer_title: title.trim() || undefined, signature });
          }}
        >
          <PenLine className="size-5" aria-hidden="true" />
          {pending ? 'Saving…' : submitLabel}
        </button>
        {!ready && <p className="text-xs text-fieldiq-muted">A name and a signature are both needed.</p>}
      </div>
    </PtFieldCard>
  );
}
